// src/screens/Session2Bridge.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Close Screen 4 — Session 2 Bridge & Actions
// Merges BusinessBridge + ThreeActions + VoyantDashboardCue into one screen.
// Brief 12: S2 target card, three actions, return to Voyant, protection note.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { ClientData } from '../types/ClientData';
import { formatCurrency } from '../utils/format';
import Scoreboard from '../components/Scoreboard';
import ProgrammeMap from '../components/ProgrammeMap';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

const Session2Bridge: React.FC<Props> = ({ data, onUpdate }) => {
  const [actions, setActions] = useState<[string, string, string]>(
    data.nextActions && data.nextActions.some(a => a)
      ? data.nextActions
      : ['', '', '']
  );

  // S2 target from Voyant
  const annualSavingsNeeded = data.voyantS1AnnualSavingsNeeded || 0;
  const monthlyTarget = annualSavingsNeeded > 0 ? Math.round(annualSavingsNeeded / 12) : 0;
  const businessMRR = data.businessRevenueMRR || 0;
  const pctOfRevenue = businessMRR > 0 ? ((monthlyTarget / businessMRR) * 100).toFixed(1) : '—';
  const freedomAge = data.targetFreedomAge || 55;

  // Pre-populate Action 3 with S2 target if empty
  const defaultAction3 = monthlyTarget > 0
    ? `Fund ${fmt(monthlyTarget)}/month employer pension from the business`
    : '';

  if (!actions[2] && defaultAction3) {
    const updated: [string, string, string] = [actions[0], actions[1], defaultAction3];
    setActions(updated);
    onUpdate({ nextActions: updated });
  }

  const handleActionChange = (index: 0 | 1 | 2, value: string) => {
    const updated: [string, string, string] = [...actions] as [string, string, string];
    updated[index] = value;
    setActions(updated);
    onUpdate({ nextActions: updated });
  };

  return (
    <div className="flex flex-col h-full">
      <Scoreboard data={data} />

      <div className="flex-1 overflow-y-auto p-6 max-w-4xl mx-auto w-full">
        {/* Programme Map — Brief 26 Section 4c */}
        <ProgrammeMap currentSession={1} completedSessions={[1]} clientName={data.clientName} />

        {/* Heading */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-ma-navy">What Happens Next</h2>
          <p className="text-gray-500 mt-1 text-sm">Your Session 2 target, three actions, and next steps.</p>
        </div>

        {/* ── Top section: S2 target (amber card) ──────────────────────────── */}
        {monthlyTarget > 0 ? (
          <div
            className="border-2 border-amber-400 bg-amber-50 rounded-2xl p-6 mb-6 text-center"
            style={{ boxShadow: '0 0 20px rgba(184, 134, 11, 0.15)' }}
          >
            <h3 className="text-sm font-bold text-amber-800 uppercase tracking-wider mb-3">
              Session 2 Target
            </h3>
            <p className="text-amber-700 text-sm mb-4">
              Your business needs to generate
            </p>
            <div className="text-4xl font-bold text-amber-900 mb-2">
              {fmt(monthlyTarget)}/month
            </div>
            <p className="text-amber-700 text-sm mb-3">
              as employer pension contribution.
            </p>
            {businessMRR > 0 && (
              <div className="bg-amber-100 rounded-xl p-3 inline-block">
                <span className="text-amber-800 text-sm">
                  That's <span className="font-bold">{pctOfRevenue}%</span> of your current{' '}
                  <span className="font-bold">{fmt(businessMRR)}/month</span> revenue.
                </span>
              </div>
            )}
            <p className="text-amber-600 text-xs mt-3 italic">
              Session 2 is where we build this into your business plan.
            </p>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6 mb-6 text-center">
            <p className="text-gray-500 text-sm italic">
              Complete Voyant Break 2 to reveal your Session 2 target.
            </p>
          </div>
        )}

        {/* ── Middle section: Three actions ─────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <h3 className="text-sm font-bold text-ma-navy mb-4 uppercase tracking-wide">
            Three Actions Before Session 2
          </h3>
          <div className="space-y-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="w-7 h-7 rounded-full bg-ma-navy text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-1">
                  {i + 1}
                </span>
                <input
                  type="text"
                  value={actions[i as 0 | 1 | 2]}
                  onChange={(e) => handleActionChange(i as 0 | 1 | 2, e.target.value)}
                  placeholder={
                    i === 0
                      ? "e.g. Set up standing orders from today's plan"
                      : i === 1
                      ? 'e.g. Review protection needs with partner'
                      : 'e.g. Business target for Session 2'
                  }
                  className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ma-navy/20"
                />
              </div>
            ))}
          </div>

          {/* Session 2 date */}
          <div className="mt-5 pt-4 border-t border-gray-100">
            <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">
              Session 2 date
            </label>
            <input
              type="text"
              value={data.session2Date || ''}
              onChange={(e) => onUpdate({ session2Date: e.target.value })}
              placeholder="e.g. 3 weeks from today"
              className="w-full mt-1.5 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ma-navy/20"
            />
          </div>
        </div>

        {/* ── Bottom section: Return to Voyant ──────────────────────────────── */}
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 mb-6">
          <h3 className="text-sm font-bold text-ma-navy mb-2">Before you go</h3>
          <p className="text-sm text-blue-700 mb-4">
            Return to Voyant to save the S1 scenario and generate the client PDF.
          </p>
          <button
            className="bg-ma-navy text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-ma-navy-light transition-colors"
            onClick={() => {
              // Placeholder — in production this would trigger PDF export
              alert('Open Voyant to export the client PDF report.');
            }}
          >
            Export client PDF from Voyant →
          </button>
        </div>

        {/* ── Protection review note ────────────────────────────────────────── */}
        <p className="text-sm text-gray-500 italic mb-6">
          Protection review: not covered today — add to Session 2 agenda.
        </p>

        {/* ── Summary snapshot ──────────────────────────────────────────────── */}
        <div className="bg-gray-50 rounded-2xl border border-gray-200 p-5 mb-6">
          <h3 className="text-sm font-bold text-ma-navy mb-3 uppercase tracking-wide">
            Session 1 Snapshot
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Freedom age</div>
              <div className="text-lg font-bold text-ma-navy">{freedomAge}</div>
            </div>
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Tax saved/year</div>
              <div className="text-lg font-bold text-green-600">{fmt(data.totalTaxSavedAnnual || 0)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Interest saved</div>
              <div className="text-lg font-bold text-green-600">{fmt(data.totalInterestSaved || 0)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">S2 monthly target</div>
              <div className="text-lg font-bold" style={{ color: '#C9A84C' }}>{fmt(monthlyTarget)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Session2Bridge;
